import React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import colors from "../../assets/theme/colors";
import { landingPageStyles } from "../../assets/theme/landingTheme";
import AudienceProviderSection from "./AudienceProviderSection";
import Footer from "./Footer";

const pageColors = {
  ink: "#17251f",
  muted: "#52645c",
  sage: colors.semantic.sageDeep,
  sageSoft: "#e8f4ef",
  lavender: colors.brand.deep,
  lavenderSoft: colors.brand.ice,
  border: "#dfe8e3",
};

const SectionLabel = ({ children }) => (
  <Typography
    sx={{
      color: pageColors.sage,
      fontSize: "0.82rem",
      fontWeight: 850,
      letterSpacing: "0.12em",
      textTransform: "uppercase",
    }}
  >
    {children}
  </Typography>
);

const BackToLandingLink = () => (
  <Button
    href="/"
    startIcon={<ArrowBackRoundedIcon />}
    sx={{
      alignSelf: "flex-start",
      borderRadius: "999px",
      px: 1.4,
      py: 0.7,
      color: pageColors.sage,
      fontWeight: 850,
      textTransform: "none",
      "&:hover": { bgcolor: pageColors.sageSoft },
    }}
  >
    Back to home
  </Button>
);

const FeatureCards = ({ cards }) => (
  <Stack spacing={2.2}>
    {cards.map(({ title, body, Icon }) => (
      <Box
        key={title}
        sx={{
          display: "grid",
          gridTemplateColumns: "52px 1fr",
          gap: 2,
          alignItems: "center",
          p: { xs: 2.2, md: 3 },
          border: `1px solid ${pageColors.border}`,
          borderRadius: 3,
          bgcolor: "#fff",
          boxShadow: "0 20px 50px rgba(31, 45, 38, 0.06)",
        }}
      >
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: pageColors.sageSoft,
            color: pageColors.sage,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box>
          <Typography sx={{ color: pageColors.ink, fontWeight: 900 }}>{title}</Typography>
          <Typography sx={{ mt: 0.4, color: pageColors.muted, lineHeight: 1.5 }}>{body}</Typography>
        </Box>
      </Box>
    ))}
  </Stack>
);

const PhoneMock = ({ title, dateRange, entries }) => (
  <Box
    sx={{
      mx: "auto",
      width: { xs: 260, md: 300 },
      p: 2,
      borderRadius: 8,
      bgcolor: pageColors.ink,
      boxShadow: "0 28px 70px rgba(31, 45, 38, 0.18)",
    }}
  >
    <Box sx={{ height: 26, width: 74, mx: "auto", mb: 2, borderRadius: 3, bgcolor: "#080d0b" }} />
    <Box sx={{ borderRadius: 5, bgcolor: "#fbfaf7", p: 2.2 }}>
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ mb: 1.4 }}>
        <Typography sx={{ color: pageColors.ink, fontWeight: 900 }}>
          {title}
        </Typography>
        <Typography sx={{ color: pageColors.muted, fontSize: "0.72rem", fontWeight: 850 }}>{dateRange}</Typography>
      </Stack>
      <Stack spacing={1.2}>
        {entries.map(([time, note, tag]) => (
          <Box key={`${time}-${tag}`} sx={{ p: 1.3, border: `1px solid ${pageColors.border}`, borderRadius: 2 }}>
            <Typography
              sx={{
                color: pageColors.sage,
                fontSize: "0.7rem",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}
            >
              {time}
            </Typography>
            <Typography sx={{ mt: 0.4, color: pageColors.ink, fontSize: "0.78rem", lineHeight: 1.35 }}>
              {note}
            </Typography>
            <Typography
              component="span"
              sx={{
                display: "inline-block",
                mt: 0.8,
                px: 0.8,
                py: 0.2,
                borderRadius: 999,
                bgcolor: pageColors.lavenderSoft,
                color: pageColors.lavender,
                fontSize: "0.62rem",
                fontWeight: 900,
              }}
            >
              {tag}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  </Box>
);

const FlowCards = ({ title, subtitle, steps }) => (
  <Box sx={{ py: { xs: 4, md: 6 }, px: { xs: 2.5, md: 5 }, borderRadius: 5, bgcolor: pageColors.sageSoft }}>
    <Stack spacing={1.2} alignItems="center" sx={{ textAlign: "center", mb: { xs: 4, md: 5 } }}>
      <Typography
        component="h2"
        sx={{
          color: pageColors.ink,
          fontSize: { xs: "2rem", md: "3rem" },
          fontWeight: 800,
          lineHeight: 1.05,
        }}
      >
        {title}
      </Typography>
      <Typography sx={{ color: pageColors.muted, fontSize: { xs: "1rem", md: "1.15rem" } }}>{subtitle}</Typography>
    </Stack>

    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: { xs: 2.5, md: 3.5 } }}>
      {steps.map((step) => (
        <Box
          key={step.title}
          sx={{
            p: 3,
            borderRadius: 3,
            bgcolor: step.highlight ? "#f1ecfb" : "#fff",
            border: step.highlight ? `1px solid ${colors.brand.tint}` : "1px solid transparent",
            color: pageColors.ink,
            textAlign: "center",
            boxShadow: "0 20px 55px rgba(31, 45, 38, 0.07)",
          }}
        >
          <Stack spacing={1.3} alignItems="center">
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: step.highlight ? "#e4d9f6" : pageColors.sageSoft,
                color: step.highlight ? pageColors.lavender : pageColors.sage,
              }}
            >
              <AssignmentOutlinedIcon fontSize="small" />
            </Box>
            <Typography
              sx={{
                color: step.highlight ? pageColors.lavender : pageColors.sage,
                fontSize: "0.72rem",
                fontWeight: 900,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
              }}
            >
              {step.label}
            </Typography>
            <Typography sx={{ fontWeight: 900, fontSize: "1.08rem" }}>{step.title}</Typography>
            <Typography sx={{ color: pageColors.muted, lineHeight: 1.55, fontSize: "0.95rem" }}>{step.body}</Typography>
          </Stack>
        </Box>
      ))}
    </Box>
  </Box>
);

const AudienceStoryPage = ({ content }) => (
  <Box sx={landingPageStyles}>
    <Container maxWidth="lg" sx={{ px: { xs: 2.5, md: 4 }, py: { xs: 5, md: 7 } }}>
      <Stack spacing={{ xs: 5, md: 7 }}>
        <BackToLandingLink />

        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            px: { xs: 2.5, md: 6 },
            py: { xs: 6, md: 9 },
            borderRadius: { xs: 4, md: 6 },
            bgcolor: pageColors.lavenderSoft,
            border: `1px solid ${colors.landing.borderLight}`,
            textAlign: "center",
          }}
        >
          <Box sx={{ position: "absolute", width: 320, height: 320, borderRadius: "50%", right: -90, top: -130, bgcolor: "rgba(125, 104, 182, 0.08)" }} />
          <Box sx={{ position: "absolute", width: 270, height: 270, borderRadius: "50%", left: -85, bottom: -140, bgcolor: "rgba(116, 170, 150, 0.12)" }} />
          <Stack spacing={2.5} alignItems="center" sx={{ position: "relative" }}>
            <SectionLabel>{content.hero.eyebrow}</SectionLabel>
            <Typography component="h1" sx={{ maxWidth: 780, color: colors.landing.heroText, fontSize: { xs: "2.1rem", md: "3.45rem" }, fontWeight: 850, lineHeight: 1.06 }}>
              {content.hero.title}
            </Typography>
            <Typography sx={{ maxWidth: 700, color: colors.landing.bodyText, fontSize: { xs: "1.05rem", md: "1.25rem" }, lineHeight: 1.6 }}>
              {content.hero.body}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ pt: 1 }}>
              {content.hero.buttons.map((button, index) => (
                <Button
                  key={button.label}
                  component="a"
                  href={button.href}
                  variant={index === 0 ? "contained" : "outlined"}
                  sx={{
                    borderRadius: "999px",
                    px: 3,
                    py: 1.2,
                    textTransform: "none",
                    fontWeight: 900,
                    color: index === 0 ? "#fff" : pageColors.sage,
                    bgcolor: index === 0 ? pageColors.lavender : "#fff",
                    borderColor: colors.landing.borderMedium,
                    boxShadow: "none",
                    "&:hover": { bgcolor: index === 0 ? pageColors.lavender : "#fff", boxShadow: "none" },
                  }}
                >
                  {button.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Box>

        <Box id="family-caregivers" sx={{ scrollMarginTop: 96 }}>
          <SectionLabel>{content.family.eyebrow}</SectionLabel>
          <Typography component="h2" sx={{ mt: 1.5, maxWidth: 760, color: pageColors.ink, fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 850, lineHeight: 1.08 }}>
            {content.family.title}
          </Typography>
          <Typography sx={{ mt: 2.2, maxWidth: 720, color: pageColors.muted, fontSize: { xs: "1.05rem", md: "1.22rem" }, lineHeight: 1.55 }}>
            {content.family.body}
          </Typography>
        </Box>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.2fr 0.8fr" }, gap: { xs: 4, lg: 7 }, alignItems: "center" }}>
          <FeatureCards cards={content.family.features} />
          <PhoneMock {...content.phone} />
        </Box>

        <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-start" } }}>
          <Button href="/login" endIcon={<ChevronRightRoundedIcon />} variant="contained" sx={{ width: { xs: "100%", sm: "auto" }, borderRadius: "999px", px: 4, py: 1.4, textTransform: "none", fontWeight: 900, bgcolor: pageColors.lavender, boxShadow: "0 18px 38px rgba(124, 106, 174, 0.18)", "&:hover": { bgcolor: pageColors.lavender, boxShadow: "0 18px 38px rgba(124, 106, 174, 0.18)" } }}>
            {content.family.cta || content.cta}
          </Button>
        </Box>

        <Box id="care-team" sx={{ scrollMarginTop: 96 }}>
          <FlowCards {...content.flow} />
        </Box>

        {content.provider && <AudienceProviderSection provider={content.provider} />}

        {!content.provider && (
          <Box sx={{ display: "flex", justifyContent: { xs: "stretch", sm: "flex-start" } }}>
            <Button href="/login" endIcon={<ChevronRightRoundedIcon />} variant="contained" sx={{ width: { xs: "100%", sm: "auto" }, borderRadius: "999px", px: 4, py: 1.4, textTransform: "none", fontWeight: 900, bgcolor: pageColors.sage, boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)", "&:hover": { bgcolor: pageColors.sage, boxShadow: "0 18px 38px rgba(111, 165, 148, 0.22)" } }}>
              {content.cta}
            </Button>
          </Box>
        )}
      </Stack>
    </Container>
    <Footer variant="desktop" />
  </Box>
);

export default AudienceStoryPage;
